import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaCamera,
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaSpinner,
  FaTimes,
  FaTruck,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { authService } from '../../../../services/apiService';
import { fraudGateService } from '../../../../services/fraudGateService';

const STEPS = [
  { id: 'photo_upload', label: 'Upload photo', num: 1 },
  { id: 'photo_check', label: 'Photo check', num: 2 },
  { id: 'label_generate', label: 'Generate label', num: 3 },
  { id: 'handoff_confirm', label: 'Confirm handoff', num: 4 },
];

function stepIndex(stepId) {
  if (stepId === 'on_hold') return 1;
  if (stepId === 'complete') return 4;
  const idx = STEPS.findIndex((s) => s.id === stepId);
  return idx >= 0 ? idx : 0;
}

export default function DispatchFlowWizard({ order, onClose, onComplete }) {
  const orderId = order?.orderId;
  const inputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoUrl, setPhotoUrl] = useState('');
  const [explanation, setExplanation] = useState('');
  const [needsExplanation, setNeedsExplanation] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const refreshStatus = useCallback(async () => {
    if (!orderId) return;
    const data = await fraudGateService.getDispatchStatus(orderId);
    setStatus(data);
    if (data?.dispatchPhotoUrl) setPhotoUrl(data.dispatchPhotoUrl);
    return data;
  }, [orderId]);

  useEffect(() => {
    (async () => {
      try {
        await refreshStatus();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Could not load dispatch status');
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshStatus]);

  const currentStep = status?.step || 'photo_upload';
  const onHold = status?.onHold || currentStep === 'on_hold';
  const activeIdx = stepIndex(currentStep);

  const uploadPhoto = async (file) => {
    if (!file?.type?.startsWith('image/')) {
      toast.error('Please upload a clear photo');
      return;
    }
    setUploading(true);
    try {
      const url = await authService.uploadCompressedFile(file);
      setPhotoUrl(url);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const runPhotoCheck = async () => {
    if (!photoUrl) {
      toast.error('Upload a dispatch photo first');
      return;
    }
    setProcessing(true);
    setCheckResult(null);
    try {
      const result = await fraudGateService.submitDispatchPhoto(orderId, {
        dispatchPhotoUrl: photoUrl,
        explanation: needsExplanation ? explanation : undefined,
      });
      setCheckResult({
        approved: true,
        result: result.comparisonResult,
        divergencePercent: result.divergencePercent,
        aiDiffScore: result.aiDiffScore,
        aiConfidence: result.aiConfidence,
      });
      toast.success('Photo approved — ready for label generation');
      await refreshStatus();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'DISPATCH_EXPLANATION_REQUIRED') {
        setNeedsExplanation(true);
        toast.warn(data.message || 'Explanation required');
        return;
      }
      if (data.code === 'DISPATCH_PHOTO_HOLD') {
        setCheckResult({
          approved: false,
          held: true,
          holdReason: data.holdReason || data.message,
          aiDiffScore: data.aiDiffScore,
          aiConfidence: data.aiConfidence,
        });
        await refreshStatus();
        return;
      }
      toast.error(data.message || err.message || 'Photo check failed');
    } finally {
      setProcessing(false);
    }
  };

  const generateLabel = async () => {
    setProcessing(true);
    try {
      const result = await fraudGateService.generateDispatchLabel(orderId);
      toast.success(`Label generated — ${result.carrier?.carrierName || 'carrier'} assigned`);
      await refreshStatus();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'CARRIER_ROUTING_FAILED') {
        toast.warn(data.message || 'Routing failed — ops notified');
        await refreshStatus();
        return;
      }
      toast.error(data.message || err.message || 'Label generation failed');
    } finally {
      setProcessing(false);
    }
  };

  const confirmHandoff = async () => {
    setProcessing(true);
    try {
      await fraudGateService.confirmDispatchHandoff(orderId);
      toast.success('Handed off to carrier — buyer tracking active');
      onComplete?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Handoff failed');
    } finally {
      setProcessing(false);
    }
  };

  const downloadLabel = async () => {
    try {
      await fraudGateService.downloadShippingLabel(orderId);
      toast.success('Label downloaded');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Download failed');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Dispatch & handoff</h3>
            <p className="text-xs text-slate-500">Order {orderId}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <FaTimes size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center">
            <FaSpinner className="animate-spin text-2xl text-indigo-600" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Stepper */}
            <div className="flex justify-between gap-1">
              {STEPS.map((s, i) => (
                <div key={s.id} className="flex-1 text-center">
                  <div
                    className={`mx-auto w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      onHold && i === 1
                        ? 'bg-red-500 text-white'
                        : i < activeIdx || (i === activeIdx && currentStep === 'complete')
                          ? 'bg-emerald-500 text-white'
                          : i === activeIdx
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {i < activeIdx ? <FaCheckCircle /> : s.num}
                  </div>
                  <p className="text-[10px] mt-1 text-slate-600 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>

            {onHold && (
              <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 space-y-2">
                <div className="flex items-center gap-2 text-red-800 font-bold">
                  <FaExclamationTriangle />
                  Dispatch on hold (E-F1)
                </div>
                <p className="text-sm text-red-700">
                  {status?.holdReason ||
                    'High-confidence photo mismatch — admin reviewing within 4 hours.'}
                </p>
                {status?.aiDiffScore != null && (
                  <p className="text-xs text-red-600">
                    AI diff score: {status.aiDiffScore}% · confidence:{' '}
                    {Math.round((status.aiConfidence || 0) * 100)}%
                  </p>
                )}
                <p className="text-xs text-red-600">
                  Further actions disabled until admin resolves.
                </p>
              </div>
            )}

            {!onHold && (currentStep === 'photo_upload' || (!status?.dispatchPhotoApproved && !checkResult)) && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Photo must show the <strong>part</strong> and <strong>packaging</strong> (bubble wrap,
                  box, or padding). Shipping label is added in step 3.
                </p>
                <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside">
                  {(status?.photoRequirements || []).map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
                {photoUrl && (
                  <img src={photoUrl} alt="Dispatch" className="w-full max-h-48 object-cover rounded-lg border" />
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                  className="w-full min-h-[52px] rounded-xl border-2 border-dashed border-indigo-300 text-indigo-700 font-semibold flex items-center justify-center gap-2"
                >
                  {uploading ? <FaSpinner className="animate-spin" /> : <FaCamera />}
                  {photoUrl ? 'Retake photo' : 'Camera or gallery'}
                </button>
                <button
                  type="button"
                  disabled={!photoUrl || processing}
                  onClick={runPhotoCheck}
                  className="w-full min-h-[52px] rounded-xl bg-indigo-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : null}
                  Continue to photo check
                </button>
              </div>
            )}

            {!onHold &&
              currentStep === 'photo_check' &&
              !status?.dispatchPhotoApproved &&
              !checkResult?.approved && (
              <div className="space-y-3 text-center py-4">
                {processing ? (
                  <>
                    <FaSpinner className="animate-spin text-3xl text-indigo-600 mx-auto" />
                    <p className="font-semibold text-slate-800">Running E-F1 photo comparison…</p>
                    <p className="text-xs text-slate-500">Perceptual hash + AI diff vs listing photos</p>
                  </>
                ) : checkResult?.held ? (
                  <div className="text-red-700 text-sm">{checkResult.holdReason}</div>
                ) : checkResult?.approved ? (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                    <FaCheckCircle className="text-3xl text-emerald-600 mx-auto mb-2" />
                    <p className="font-bold text-emerald-800">Approved</p>
                    <p className="text-xs text-emerald-700 mt-1">
                      Match within tolerance · {checkResult.divergencePercent}% divergence
                    </p>
                  </div>
                ) : null}
                {needsExplanation && !checkResult?.approved && (
                  <textarea
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    placeholder="Explain moderate photo difference…"
                  />
                )}
              </div>
            )}

            {!onHold &&
              (currentStep === 'label_generate' ||
                (status?.dispatchPhotoApproved && !status?.labelGenerated)) && (
              <div className="space-y-3">
                {status?.dispatchPhotoApproved && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2">
                    <FaCheckCircle /> Photo verified (E-F1)
                  </div>
                )}
                <p className="text-sm text-slate-600">
                  Carrier auto-selected from weight, fragility, distance, and COD requirement.
                </p>
                {status?.carrierRouting?.status === 'failed' && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                    Routing failed — ops escalated. Fallback carrier within 3 hours.
                  </div>
                )}
                <button
                  type="button"
                  disabled={processing || !status?.canGenerateLabel}
                  onClick={generateLabel}
                  className="w-full min-h-[52px] rounded-xl bg-indigo-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaTruck />}
                  Generate shipping label
                </button>
              </div>
            )}

            {!onHold && (currentStep === 'handoff_confirm' || status?.labelGenerated) && (
              <div className="space-y-3">
                {status?.carrier && (
                  <div className="rounded-xl bg-slate-50 border p-4 space-y-1 text-sm">
                    <p className="font-bold text-slate-900">{status.carrier.carrierName}</p>
                    <p className="text-slate-600">AWB: {status.carrier.awb}</p>
                    <p className="text-slate-500 text-xs">
                      Destination: {status.maskedDestination?.cityState},{' '}
                      {status.maskedDestination?.postalCode}
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={downloadLabel}
                  className="w-full py-3 rounded-xl border border-indigo-300 text-indigo-700 font-semibold flex items-center justify-center gap-2"
                >
                  <FaDownload /> Download label PDF
                </button>
                <button
                  type="button"
                  disabled={processing || !status?.canConfirmHandoff}
                  onClick={confirmHandoff}
                  className="w-full min-h-[52px] rounded-xl bg-emerald-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                  Confirm handoff to carrier
                </button>
                <p className="text-[10px] text-slate-500 text-center">
                  Buyer tracking link activates · contact masking enabled
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
